import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Calendar, Clock, User } from "lucide-react";

interface StudentEmail {
  email: string;
  student_name: string | null;
}

interface Subject {
  id: string;
  name: string;
  grade: string;
}

interface StudentList {
  id: string;
  list_name: string;
  description: string | null;
}

interface InteractiveSchedulingCalendarProps {
  existingSchedules: any[];
  onScheduleCreated?: () => void;
}

export default function InteractiveSchedulingCalendar({ 
  existingSchedules, 
  onScheduleCreated 
}: InteractiveSchedulingCalendarProps) {
  const { user } = useAuth();
  const [showDialog, setShowDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [schedulingMode, setSchedulingMode] = useState<'individual' | 'bulk'>('individual');
  const [studentEmails, setStudentEmails] = useState<StudentEmail[]>([]);
  const [studentLists, setStudentLists] = useState<StudentList[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    student_email: '',
    student_list_id: '',
    duration: '60',
    subject: '',
    topic: '',
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    
    try {
      // Load student emails
      const { data: emailData } = await supabase
        .from('instructor_student_emails')
        .select(`
          email,
          student_name,
          instructor_student_lists!inner(instructor_id)
        `)
        .eq('instructor_student_lists.instructor_id', user.id);
      
      if (emailData) {
        const uniqueEmails = Array.from(
          new Map(emailData.map(item => [item.email, item])).values()
        );
        setStudentEmails(uniqueEmails);
      }

      // Load student lists
      const { data: listData } = await supabase
        .from('instructor_student_lists')
        .select('id, list_name, description')
        .eq('instructor_id', user.id);
      
      if (listData) setStudentLists(listData);

      // Load subjects
      const { data: subjectData } = await supabase
        .from('subjects')
        .select('id, name, grade')
        .order('grade')
        .order('name');
      
      if (subjectData) setSubjects(subjectData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load scheduling data');
    }
  };

  const handleDateClick = (dateInfo: any) => {
    const clickedDate = dateInfo.dateStr;
    const now = new Date();
    const selectedDateTime = new Date(clickedDate);
    
    if (selectedDateTime < now) {
      toast.error('Cannot schedule sessions in the past');
      return;
    }

    setSelectedDate(clickedDate);
    setSelectedTime('09:00');
    setShowDialog(true);
    setFormData({
      student_email: '',
      student_list_id: '',
      duration: '60',
      subject: '',
      topic: '',
      notes: '',
    });
  };

  const handleScheduleSubmit = async () => {
    if (!user || !selectedDate || !selectedTime) return;

    if (schedulingMode === 'individual' && !formData.student_email) {
      toast.error('Please select a student');
      return;
    }

    if (schedulingMode === 'bulk' && !formData.student_list_id) {
      toast.error('Please select a student list');
      return;
    }

    if (!formData.subject) {
      toast.error('Please select a subject');
      return;
    }

    setLoading(true);

    try {
      const duration = parseInt(formData.duration);
      const startTime = selectedTime;
      const endTime = calculateEndTime(startTime, duration);
      const selectedSubject = subjects.find(s => s.id === formData.subject);
      const subjectName = selectedSubject?.name || formData.subject;

      if (schedulingMode === 'individual') {
        // Create individual schedule
        const selectedStudent = studentEmails.find(s => s.email === formData.student_email);
        
        const { error } = await supabase
          .from('tutoring_schedules')
          .insert({
            instructor_id: user.id,
            student_email: formData.student_email,
            student_name: selectedStudent?.student_name,
            session_date: selectedDate,
            start_time: startTime,
            end_time: endTime,
            subject: subjectName,
            topic: formData.topic || null,
            notes: formData.notes || null,
            status: 'scheduled'
          });

        if (error) throw error;
        toast.success('Session scheduled successfully!');
      } else {
        // Create bulk schedule for entire student list (single entry)
        const { error } = await supabase
          .from('student_list_schedules')
          .insert({
            instructor_id: user.id,
            list_id: formData.student_list_id,
            session_date: selectedDate,
            start_time: startTime,
            end_time: endTime,
            subject: subjectName,
            topic: formData.topic || null,
            notes: formData.notes || null,
            recurrence_pattern: 'once'
          });

        if (error) throw error;
        toast.success('Student list session scheduled successfully!');
      }

      setShowDialog(false);
      onScheduleCreated?.();
    } catch (error) {
      console.error('Error scheduling session:', error);
      toast.error('Failed to schedule session');
    } finally {
      setLoading(false);
    }
  };

  const calculateEndTime = (startTime: string, durationMinutes: number): string => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes);
    startDate.setMinutes(startDate.getMinutes() + durationMinutes);
    
    return startDate.toTimeString().substring(0, 5);
  };

  const calendarEvents = existingSchedules.map(schedule => ({
    id: schedule.id,
    title: `${schedule.student_name || schedule.student_email} - ${schedule.subject}`,
    start: `${schedule.session_date}T${schedule.start_time}`,
    end: `${schedule.session_date}T${schedule.end_time}`,
    backgroundColor: schedule.status === 'completed' ? '#10b981' : 
                    schedule.status === 'cancelled' ? '#ef4444' : '#3b82f6'
  }));

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Interactive Scheduling Calendar
          </CardTitle>
          <CardDescription>
            Click on any date to schedule a tutoring session. View existing sessions on the calendar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="calendar-container">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay'
              }}
              events={calendarEvents}
              dateClick={handleDateClick}
              height="600px"
              selectable={true}
              selectMirror={true}
              dayMaxEvents={true}
              weekends={true}
              eventDisplay="block"
              eventTextColor="#ffffff"
              eventBorderColor="transparent"
              dayHeaderClassNames="text-sm font-medium text-gray-700"
              dayCellClassNames="hover:bg-gray-50 cursor-pointer"
            />
          </div>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Schedule Session
            </DialogTitle>
            <DialogDescription>
              Create a tutoring session for {selectedDate}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Scheduling Mode</Label>
              <Select value={schedulingMode} onValueChange={(value: 'individual' | 'bulk') => setSchedulingMode(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual Student</SelectItem>
                  <SelectItem value="bulk">Entire Student List</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {schedulingMode === 'individual' ? (
              <div className="space-y-2">
                <Label htmlFor="student_email" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Student *
                </Label>
                <Select value={formData.student_email} onValueChange={(value) => setFormData(prev => ({ ...prev, student_email: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select student" />
                  </SelectTrigger>
                  <SelectContent>
                    {studentEmails.map((student) => (
                      <SelectItem key={student.email} value={student.email}>
                        {student.student_name ? `${student.student_name} (${student.email})` : student.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="student_list_id" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Student List *
                </Label>
                <Select value={formData.student_list_id} onValueChange={(value) => setFormData(prev => ({ ...prev, student_list_id: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select student list" />
                  </SelectTrigger>
                  <SelectContent>
                    {studentLists.map((list) => (
                      <SelectItem key={list.id} value={list.id}>
                        {list.list_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="time" className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Start Time *
                </Label>
                <Input
                  type="time"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Select value={formData.duration} onValueChange={(value) => setFormData(prev => ({ ...prev, duration: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Subject *</Label>
              <Select value={formData.subject} onValueChange={(value) => setFormData(prev => ({ ...prev, subject: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select subject" />
                </SelectTrigger>
                <SelectContent>
                  {subjects.map((subject) => (
                    <SelectItem key={subject.id} value={subject.id}>
                      {subject.name} (Grade {subject.grade})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="topic">Topic</Label>
              <Input
                value={formData.topic}
                onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                placeholder="Optional topic"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes"
                rows={3}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleScheduleSubmit} disabled={loading}>
                {loading ? 'Scheduling...' : 'Schedule Session'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        .calendar-container .fc-toolbar {
          margin-bottom: 1rem;
        }
        .calendar-container .fc-toolbar-title {
          font-size: 1.25rem;
          font-weight: 600;
        }
        .calendar-container .fc-button {
          background-color: hsl(var(--primary));
          border-color: hsl(var(--primary));
          color: hsl(var(--primary-foreground));
        }
        .calendar-container .fc-button:hover {
          background-color: hsl(var(--primary) / 0.9);
        }
        .calendar-container .fc-button:disabled {
          opacity: 0.5;
        }
        .calendar-container .fc-daygrid-day:hover {
          background-color: hsl(var(--muted) / 0.5);
        }
        .calendar-container .fc-event {
          border-radius: 4px;
          padding: 2px 4px;
          font-size: 0.875rem;
        }
      `}</style>
    </>
  );
}